import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { useT, type StringId } from '../lib/i18n'
import { ShellHeader } from '../ui/ShellHeader'
import { Spinner } from '../ui/Spinner'
import { useGates } from './GateContext'

/**
 * What a person sees where the fasting feature would be.
 *
 * `docs/SCIENCE.md` §3.4 splits its table in two, and until `SUAP-1214` this
 * screen rendered only half of it. Six conditions are **blocked**; four are
 * **gated** — *"behind an explicit 'my clinician approved this' attestation +
 * hypoglycaemia warning"*. `SUAP-1202` built that on the server and the client
 * showed no button, so a person on insulin whose clinician had approved a
 * protocol was treated exactly like a person who is pregnant. The half that had
 * disappeared was the one that says yes.
 *
 * The tone rule is §3.4's and it is the hardest part: *"A blocked user still gets
 * the full calorie/macro/micro product. **We remove the fasting feature, not the
 * person.** Copy must never read as a rejection."* So the screen says what is
 * unavailable, why in terms of the situation, what the person can do about it if
 * anything, and what still works — and never apologises.
 */

/** Maps a server reason code to the sentence that explains it. */
const REASON_COPY: Record<string, StringId> = {
  not_screened: 'gate.reason.notScreened',
  blocked: 'gate.reason.blocked',
  needs_attestation: 'gate.reason.needsAttestation',
}

/**
 * Per-gate explanation, keyed by the server's gate id so a gate added to
 * `model.Gate` without copy here is a missing key rather than a blank line.
 */
const GATE_COPY: Record<string, StringId> = {
  fasting_blocked_pregnancy: 'gate.pregnancy',
  fasting_blocked_breastfeeding: 'gate.breastfeeding',
  fasting_blocked_under_18: 'gate.under18',
  fasting_blocked_ed_history: 'gate.edHistory',
  fasting_blocked_bmi: 'gate.underweight',
  fasting_blocked_type_1_diabetes: 'gate.type1',
  fasting_attest_hypoglycaemia_risk: 'gate.hypoRisk',
  fasting_attest_clinician: 'gate.clinician',
}

/**
 * The warning §3.4 requires *"+ hypoglycaemia warning"* for the
 * insulin/sulfonylurea row, shown **before** the approval and not after it.
 *
 * Only that gate has one. `fasting_attest_clinician` covers advanced kidney
 * disease, active cancer and frailty, which §3.4 gates with *"advise clinician"*
 * and no specific warning — inventing one for them would be this file making a
 * clinical claim, which `docs/agents/WORKFLOW.md` § Escalate forbids.
 */
const GATE_WARNING: Record<string, StringId> = {
  fasting_attest_hypoglycaemia_risk: 'gate.hypoRisk.warning',
}

function GateRow({ gate, attested }: { gate: string; attested: boolean }) {
  const { t } = useT()
  const { attest, revoke } = useGates()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  const run = async (action: (g: string) => Promise<void>) => {
    setBusy(true)
    setFailed(false)
    try {
      await action(gate)
    } catch {
      // The server is the authority and it refused. Say so, and leave the gate
      // where it was — an optimistic update here would show fasting as available
      // to somebody the server will keep refusing.
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const warning = GATE_WARNING[gate]

  return (
    <Card>
      <p className="text-ink">{t(GATE_COPY[gate] ?? 'gate.unknown')}</p>

      {/* Before the action, never after it. §3.4 asks for the warning alongside
          the attestation, and a warning that appears once you have already
          agreed is a receipt rather than a warning. */}
      {!attested && warning !== undefined ? (
        <p className="mt-2 text-sm text-ink-soft">{t(warning)}</p>
      ) : null}

      {attested ? (
        <>
          {/* The clearance stays visible. A gate that silently disappears once
              cleared leaves the person with no way to see what they asserted, or
              to change their mind about it. */}
          <p className="mt-2 text-sm text-ink-soft">{t('gate.attested.state')}</p>
          <div className="mt-3">
            <Button variant="ghost" onClick={() => void run(revoke)} loading={busy}>
              {t('gate.attested.withdraw')}
            </Button>
          </div>
        </>
      ) : (
        <div className="mt-3">
          <Button variant="ink" onClick={() => void run(attest)} loading={busy}>
            {t('gate.attest.action')}
          </Button>
        </div>
      )}

      {failed ? (
        <p className="mt-2 text-sm text-over" role="alert">
          {t('gate.attest.failed')}
        </p>
      ) : null}
    </Card>
  )
}

function GateNotice() {
  const { t } = useT()
  const { state } = useGates()

  const reasonKey = REASON_COPY[state?.reason ?? ''] ?? 'gate.reason.blocked'
  const blocked = state?.blocked ?? []
  const awaiting = state?.awaiting_attestation ?? []
  const attested = state?.attested ?? []

  return (
    <>
      <ShellHeader>
        <h1 className="text-lg font-semibold text-ink">{t('screen.fasting')}</h1>
      </ShellHeader>
      <section className="flex flex-col gap-4 p-4">
        <p className="text-ink">{t(reasonKey)}</p>

        {/* **Blocked gates carry no control at all** — not a disabled one. §3.4's
            six hard blocks have no override path, and a greyed-out button is an
            override path the user can see and ask about. The server refuses them
            too (`gate_not_attestable`), which is the guarantee; this is the half
            that stops the question being raised. */}
        {blocked.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {blocked.map((gate) => (
              <li key={gate} className="text-ink-soft">
                {t(GATE_COPY[gate] ?? 'gate.unknown')}
              </li>
            ))}
          </ul>
        ) : null}

        {/* **No approval control while a hard block stands.**
            §3.4's blocks have no override path, so attesting an *attestable* gate
            alongside one changes nothing: the person would press the button, the
            server would record the attestation, and fasting would stay exactly as
            unavailable as before.
            Offering it anyway is a false affordance — worse here than a dead
            button elsewhere, because the thing it appears to promise is a feature
            the person has just been told they cannot have, and the reason they
            cannot is pregnancy or an eating-disorder history.
            Found by driving a user who is both pregnant and on insulin: the
            screen showed the blocks *and* an approval button. */}
        {blocked.length === 0
          ? [
              ...awaiting.map((gate) => <GateRow key={gate} gate={gate} attested={false} />),
              ...attested.map((gate) => <GateRow key={gate} gate={gate} attested />),
            ]
          : null}

        {/* §3.4's last line, rendered. This paragraph is why the screen exists
            rather than a 404. */}
        <p className="text-ink-soft">{t('gate.stillAvailable')}</p>
      </section>
    </>
  )
}

/**
 * The route guard.
 *
 * A layout route, the same shape as `ProtectedRoute`: a fasting screen added
 * later is gated by **where it is declared**, and forgetting is not an option
 * that exists.
 *
 * **This is not the enforcement.** `middleware.RequireUngated` is, and it holds
 * when the browser is bypassed. What this does is stop a gated surface being
 * rendered-and-disabled — a disabled fasting timer still shows somebody a
 * fasting timer.
 */
export function FastingGate() {
  const { t } = useT()
  const { status, state } = useGates()

  if (status === 'loading') {
    return (
      <div className="flex min-h-40 items-center justify-center" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">{t('state.loading')}</span>
      </div>
    )
  }

  // `unavailable` — we could not read the gate state — renders the notice too.
  // Not knowing is not permission.
  if (status !== 'ready' || state?.fasting_available !== true) return <GateNotice />

  return <Outlet />
}
